/* =========================================================
   CORE FOUR – ORIGINALZ · Scoped PDP controller
   ---------------------------------------------------------
   Custom bundle-style PDP driven by two dedicated SET products
   (same model as the Core Four Cottonz PDP) — no bundle app:
     Core Four Originalz (4-pair set) — the required product
     Core Five Originalz (5-pair set) — the Black-add-on upgrade
   The "Add Originalz Black" checkbox is an UPGRADE toggle: it
   swaps the cart target from the Core Four variant to the Core
   Five variant in the chosen size. Add to Cart posts ONE
   /cart/add.js line item either way.

   The products are emitted by the MAIN section as a JSON blob:

     <script type="application/json" data-cfo-products>
       {
         "required": [
           { "key":"core-four", "handle":"...", "title":"...",
             "sizes": { "S": {"id":123,"available":true}, ... } }
         ],
         "addon": { "key":"core-five", "handle":"...", "title":"...",
                    "sizes": { "S": {"id":123,"available":true}, ... } }
       }
     </script>

   A size is BUYABLE when the Core Four set has an available
   variant for it. The Black add-on is offered per size only when
   the Core Five set is available in that size.

   Everything is scoped to the MAIN section root
   [data-cfo-root]. Presentational sections (colorways, benefits,
   details, fit, faq) carry no JS. The add-on and final-CTA
   sections drive this controller through document-level
   [data-cfo-trigger] delegation, so their buttons operate the
   single buy-box form. If the MAIN section is absent, triggers
   degrade gracefully (no-op / console warning).

   NO external libraries. Safe for Shopify section reloads.
   ========================================================= */
(function () {
  'use strict';

  var SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
  var registry = {}; // sectionId -> controller

  // ---------- CART DRAWER HOOK — Dawn 13.x -------------------
  // Same pattern as the Core Four Cottonz PDP: parsedState comes from
  // the /cart/add.js response (requested with sections:['cart-drawer',
  // 'cart-icon-bubble'], sections_url:'/') and already contains the
  // rendered drawer HTML.
  function onCartAdded(root, parsedState) {
    var evt = new CustomEvent('drawlz:cfo:added', { bubbles: true, detail: parsedState });
    root.dispatchEvent(evt);
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) { window.location.href = '/cart'; return; }
    if (typeof cartDrawer.renderContents === 'function' && parsedState && parsedState.sections) {
      try {
        cartDrawer.renderContents(parsedState);
      } catch (e) {
        // renderContents threw (e.g. cart-icon-bubble element missing) — still open
        if (typeof cartDrawer.open === 'function') cartDrawer.open();
      }
      // This theme's cart-drawer.liquid puts `is-empty` on the <cart-drawer>
      // element itself when the cart is empty at page load. Dawn's
      // renderContents only removes it from `.drawer__inner`, so the class
      // stays and component-cart-drawer.css hides the header / collapses
      // the items area. Strip it here (same fix as the Cottonz PDP).
      cartDrawer.classList.remove('is-empty');
    } else if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      window.location.href = '/cart';
    }
  }

  function money(v) { return v; } // price text is pre-rendered by Liquid; JS only swaps strings

  function parseProducts(root) {
    var el = root.querySelector('[data-cfo-products]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { console.warn('[CFO] product JSON parse failed', e); return null; }
  }

  function Controller(root) {
    this.root = root;
    this.id = root.getAttribute('data-cfo-root');
    this.data = parseProducts(root);
    this.state = { size: null, addon: false, busy: false };
    if (!this.data || !this.data.required || this.data.required.length === 0) {
      console.warn('[CFO] No product data found for section', this.id,
        '— size selection and add-to-cart are disabled until product handles are wired.');
    }
    this.cache();
    this.computeSizes();
    this.bind();
    this.renderSizes();
    this.refresh();
  }

  Controller.prototype.cache = function () {
    var r = this.root;
    this.$sizes        = r.querySelectorAll('[data-cfo-sizes] [data-cfo-size]');
    this.$sizeLabel    = r.querySelectorAll('[data-cfo-size-label]');
    this.$addonWrap    = r.querySelector('[data-cfo-addon]');
    this.$addonToggle  = r.querySelector('[data-cfo-addon-toggle]');
    this.$addonSub     = r.querySelector('[data-cfo-addon-sub]');
    this.$add          = r.querySelector('[data-cfo-add]');
    this.$addLabel     = r.querySelector('[data-cfo-add-label]');
    this.$price        = r.querySelectorAll('[data-cfo-price]');
    this.$perPair      = r.querySelectorAll('[data-cfo-perpair]');
    this.$status       = r.querySelector('[data-cfo-status]');
    this.$sizeError    = r.querySelector('[data-cfo-size-error]');
    // Gallery
    this.$thumbs       = r.querySelector('[data-cfo-thumbs]');
    this.$mainImg      = r.querySelector('[data-cfo-main-img]');
    this.$galleryMain  = r.querySelector('[data-cfo-gallery-main]');
    this.$galleryCount = r.querySelector('[data-cfo-gallery-count]');
    // Sticky
    this.$sticky       = r.querySelector('[data-cfo-sticky]');
    this.$stickySize   = r.querySelector('[data-cfo-sticky-size]');
    this.$hero         = r.querySelector('[data-cfo-hero]');
    // Pre-rendered price strings (from Liquid, via data-attrs)
    this.priceIncl  = r.getAttribute('data-cfo-price-pack')   || '';
    this.priceFull  = r.getAttribute('data-cfo-price-full')   || '';
    this.perPack    = r.getAttribute('data-cfo-perpair-pack') || '';
    this.perFull    = r.getAttribute('data-cfo-perpair-full') || '';
    this.addLabelPack = r.getAttribute('data-cfo-addlabel-pack') || 'Add Core Four – Originalz';
    this.addLabelFull = r.getAttribute('data-cfo-addlabel-full') || 'Add Core Four + Originalz Black';
  };

  // Union of sizes present, ordered; each flagged buyable/blackAvailable
  Controller.prototype.computeSizes = function () {
    var d = this.data;
    this.sizeInfo = {};
    if (!d) return;
    var seen = {};
    d.required.forEach(function (p) { Object.keys(p.sizes || {}).forEach(function (s) { seen[s] = true; }); });
    if (d.addon) Object.keys(d.addon.sizes || {}).forEach(function (s) { seen[s] = true; });
    var self = this;
    Object.keys(seen).forEach(function (s) {
      var buyable = d.required.every(function (p) {
        var v = p.sizes && p.sizes[s];
        return v && v.available && v.id;
      });
      var black = d.addon && d.addon.sizes && d.addon.sizes[s] && d.addon.sizes[s].available && d.addon.sizes[s].id;
      self.sizeInfo[s] = { buyable: buyable, black: !!black };
    });
  };

  Controller.prototype.orderedSizes = function () {
    var keys = Object.keys(this.sizeInfo || {});
    return keys.sort(function (a, b) {
      var ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
      if (ia === -1) ia = 99; if (ib === -1) ib = 99;
      return ia - ib;
    });
  };

  // All size buttons this controller owns: inside its root, plus any in
  // presentational .core-four-originalz-pdp sections that have no root of
  // their own (e.g. the final-CTA mobile size pills).
  Controller.prototype.allSizeButtons = function () {
    var inRoot = Array.prototype.slice.call(this.root.querySelectorAll('[data-cfo-size]'));
    var orphans = Array.prototype.slice.call(document.querySelectorAll('.core-four-originalz-pdp [data-cfo-size]'))
      .filter(function (b) { return !b.closest('[data-cfo-root]'); });
    return inRoot.concat(orphans);
  };
  Controller.prototype.allSizeLabels = function () {
    var inRoot = Array.prototype.slice.call(this.root.querySelectorAll('[data-cfo-size-label]'));
    var orphans = Array.prototype.slice.call(document.querySelectorAll('.core-four-originalz-pdp [data-cfo-size-label]'))
      .filter(function (b) { return !b.closest('[data-cfo-root]'); });
    return inRoot.concat(orphans);
  };

  Controller.prototype.renderSizes = function () {
    var self = this;
    var buttons = this.allSizeButtons();
    if (!buttons.length) return;
    buttons.forEach(function (btn) {
      var s = btn.getAttribute('data-cfo-size');
      var info = self.sizeInfo[s];
      var buyable = info ? info.buyable : false;
      btn.disabled = !buyable;
      btn.setAttribute('aria-disabled', String(!buyable));
      btn.classList.toggle('cfo-oos', !buyable);
      if (!buyable) btn.setAttribute('title', 'Unavailable in one or more colorways');
      else btn.removeAttribute('title');
    });
  };

  Controller.prototype.variantIdsForSize = function (size) {
    // returns { ok, items: [{id,quantity,properties}], missing:[] }
    var d = this.data, missing = [], items = [];
    if (!d || !size) return { ok: false, items: [], missing: ['size'] };
    d.required.forEach(function (p) {
      var v = p.sizes && p.sizes[size];
      if (v && v.available && v.id) {
        items.push({ id: v.id, quantity: 1, properties: {
          '_bundle': 'Core Four Originalz',
          '_bundle_size': size,
          '_bundle_type': 'Core Four'
        }});
      } else { missing.push(p.key); }
    });
    return { ok: missing.length === 0, items: items, missing: missing };
  };

  // Core Five set variant for the chosen size (the Black "add-on" is an
  // UPGRADE: it swaps the cart target to this product, Cottonz-style).
  Controller.prototype.blackItemForSize = function (size) {
    var d = this.data;
    if (!d || !d.addon || !size) return null;
    var v = d.addon.sizes && d.addon.sizes[size];
    if (v && v.available && v.id) {
      return { id: v.id, quantity: 1, properties: {
        '_bundle': 'Core Five Originalz',
        '_bundle_size': size,
        '_bundle_type': 'Core Five',
        '_addon': 'Originalz Black'
      }};
    }
    return null;
  };

  Controller.prototype.setSize = function (size, opts) {
    opts = opts || {};
    if (!size) return;
    var info = this.sizeInfo[size];
    if (!info || !info.buyable) return; // ignore unavailable
    this.state.size = size;
    // reflect active state across every size grid this controller owns
    // (buy box + any presentational sections, e.g. final-CTA pills)
    this.allSizeButtons().forEach(function (b) {
      b.classList.toggle('cfo-active', b.getAttribute('data-cfo-size') === size);
    });
    var label = null;
    this.$sizes.forEach(function (b) { if (b.getAttribute('data-cfo-size') === size) label = b.getAttribute('data-cfo-label') || size; });
    this.allSizeLabels().forEach(function (el) { el.textContent = label || size; });
    if (this.$stickySize) this.$stickySize.textContent = label || size;
    // If Black not available in this size, force addon off + disable
    if (!info.black && this.state.addon) this.state.addon = false;
    this.refresh();
    if (this.$sizeError) this.$sizeError.hidden = true;
  };

  Controller.prototype.setAddon = function (on, opts) {
    opts = opts || {};
    var size = this.state.size;
    // Can only turn on if a size is chosen AND black available in it
    if (on && (!size || !(this.sizeInfo[size] && this.sizeInfo[size].black))) {
      // bounce back
      if (this.$addonToggle) this.$addonToggle.checked = false;
      this.state.addon = false;
      if (opts.fromTrigger && !size) this.flashSizeError();
      this.refresh();
      return;
    }
    this.state.addon = !!on;
    if (this.$addonToggle) this.$addonToggle.checked = !!on;
    this.refresh();
  };

  Controller.prototype.flashSizeError = function () {
    if (this.$sizeError) {
      this.$sizeError.hidden = false;
    }
    var block = this.root.querySelector('[data-cfo-size-block]');
    if (block) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  Controller.prototype.refresh = function () {
    var size = this.state.size;
    var addon = this.state.addon;
    // Add-on availability gating
    if (this.$addonWrap) {
      var blackOk = size && this.sizeInfo[size] && this.sizeInfo[size].black;
      this.$addonWrap.setAttribute('data-state', !size ? 'pending' : (blackOk ? 'ready' : 'unavailable'));
      if (this.$addonToggle) this.$addonToggle.disabled = !blackOk;
      if (this.$addonSub) {
        if (!size) this.$addonSub.textContent = this.$addonSub.getAttribute('data-msg-pending') || 'Select a size to add the black anchor.';
        else if (!blackOk) this.$addonSub.textContent = this.$addonSub.getAttribute('data-msg-oos') || 'Originalz Black is unavailable in this size.';
        else this.$addonSub.textContent = this.$addonSub.getAttribute('data-msg-ready') || this.$addonSub.getAttribute('data-msg-default') || this.$addonSub.textContent;
      }
    }
    // Price strings
    var priceStr = addon ? this.priceFull : this.priceIncl;
    var perStr   = addon ? this.perFull : this.perPack;
    if (priceStr) this.$price.forEach(function (el) { el.textContent = priceStr; });
    if (perStr) this.$perPair.forEach(function (el) { el.textContent = perStr; });
    // Add button label + enabled
    var canBuy = !!(size && this.variantIdsForSize(size).ok) && !this.state.busy;
    if (this.$add) {
      this.$add.disabled = !canBuy;
      this.$add.setAttribute('aria-disabled', String(!canBuy));
    }
    if (this.$addLabel) {
      this.$addLabel.textContent = addon ? this.addLabelFull : this.addLabelPack;
    }
    // reflect on any bundle/cross-section add labels
    this.root.querySelectorAll('[data-cfo-add-label-mirror]').forEach(function (el) {
      el.textContent = addon ? this.addLabelFull : this.addLabelPack;
    }.bind(this));
  };

  Controller.prototype.addToCart = function (opts) {
    opts = opts || {};
    var self = this;
    var size = this.state.size;
    if (this.state.busy) return;
    if (!size) { this.flashSizeError(); return; }
    var req = this.variantIdsForSize(size);
    if (!req.ok) {
      this.setStatus('error', 'That size is unavailable in one or more colorways. Please choose another size.');
      return;
    }
    // Upgrade model (same as Cottonz): with the Black add-on ON the cart
    // target SWAPS from the Core Four set product to the Core Five set
    // product — one line item either way, never a separate Black item.
    var items = req.items.slice();
    if (this.state.addon) {
      var five = this.blackItemForSize(size);
      if (five) items = [five];
    }
    this.state.busy = true;
    this.refresh();
    this.setStatus('loading', opts.loadingText || 'Adding to cart…');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: '/' })
    })
    .then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw e; });
      return res.json();
    })
    .then(function (payload) {
      self.state.busy = false;
      self.setStatus('success', 'Added to cart');
      onCartAdded(self.root, payload);
    })
    .catch(function (err) {
      self.state.busy = false;
      self.refresh();
      var msg = (err && (err.description || err.message)) || 'Something went wrong. Please try again.';
      self.setStatus('error', msg);
      console.warn('[CFO] cart/add failed', err);
    });
  };

  Controller.prototype.setStatus = function (kind, msg) {
    if (!this.$status) return;
    this.$status.textContent = msg || '';
    this.$status.setAttribute('data-kind', kind || '');
    this.$status.hidden = !msg;
  };

  // ---------- Gallery ----------
  // Same UX as the Cottonz PDP: desktop thumbs column; mobile hides
  // thumbs and uses overlay prev/next arrows + dots + touch swipe on
  // the main image. The tinted backdrop (per-image colorway tint) is
  // an intentional Originalz design difference from Cottonz.
  Controller.prototype.bindGallery = function () {
    var self = this;
    if (!this.$thumbs || !this.$mainImg) return;
    var thumbs = Array.prototype.slice.call(this.$thumbs.querySelectorAll('[data-cfo-thumb]'));
    if (!thumbs.length) return;
    var total = thumbs.length;
    var dotsEl = this.root.querySelector('[data-cfo-gallery-dots]');
    var current = 0;
    function renderDots() {
      if (!dotsEl) return;
      dotsEl.innerHTML = '';
      thumbs.forEach(function (_, i) {
        var dot = document.createElement('span');
        dot.className = 'cfo-gallery-dot' + (i === current ? ' cfo-active' : '');
        dot.addEventListener('click', function () { select(i); });
        dotsEl.appendChild(dot);
      });
    }
    function select(i) {
      current = ((i % total) + total) % total;
      var t = thumbs[current]; if (!t) return;
      thumbs.forEach(function (x) { x.classList.remove('cfo-active'); x.setAttribute('aria-current', 'false'); });
      t.classList.add('cfo-active'); t.setAttribute('aria-current', 'true');
      var src = t.getAttribute('data-cfo-src'); var alt = t.getAttribute('data-cfo-alt') || '';
      var tint = t.getAttribute('data-cfo-tint');
      if (src) { self.$mainImg.src = src; self.$mainImg.alt = alt; }
      // Colorway-tinted gallery backdrop — intentional Originalz design
      // difference from the Cottonz flat cream (soft 16% mix keeps it subtle).
      if (tint && self.$galleryMain) self.$galleryMain.style.setProperty('--cfo-gallery-tint', 'color-mix(in srgb, ' + tint + ' 16%, #ffffff)');
      if (self.$galleryCount) self.$galleryCount.textContent = (current + 1) + ' / ' + total;
      renderDots();
    }
    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { select(i); });
      t.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(i); } });
    });
    // Prev/next arrows (visible on mobile)
    var prevBtn = this.root.querySelector('[data-cfo-gallery-prev]');
    var nextBtn = this.root.querySelector('[data-cfo-gallery-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { select(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { select(current + 1); });
    // Touch swipe on the main image (Cottonz pattern)
    if (this.$galleryMain) {
      var touchX = null;
      this.$galleryMain.addEventListener('touchstart', function (e) { touchX = e.touches[0].clientX; }, { passive: true });
      this.$galleryMain.addEventListener('touchend', function (e) {
        if (touchX == null) return;
        var dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 40) select(current + (dx < 0 ? 1 : -1));
        touchX = null;
      });
    }
    select(0);
  };

  // ---------- Sticky mobile ATC ----------
  Controller.prototype.bindSticky = function () {
    var self = this;
    if (!this.$sticky || !this.$hero) return;
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        self.$sticky.classList.toggle('cfo-show', !en.isIntersecting && en.boundingClientRect.top < 0);
      });
    }, { threshold: 0 });
    io.observe(this.$hero);
    this._io = io;
  };

  Controller.prototype.bind = function () {
    var self = this;
    // Size grids inside this root
    this.root.querySelectorAll('[data-cfo-sizes]').forEach(function (grid) {
      grid.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-cfo-size]');
        if (btn && !btn.disabled) self.setSize(btn.getAttribute('data-cfo-size'));
      });
    });
    // Add-on toggle
    if (this.$addonToggle) {
      this.$addonToggle.addEventListener('change', function (e) { self.setAddon(e.target.checked); });
    }
    // Main add button
    if (this.$add) this.$add.addEventListener('click', function () { self.addToCart(); });
    // Size-guide + generic scroll-to-buy inside root
    this.root.querySelectorAll('[data-cfo-scroll-to-buy]').forEach(function (el) {
      el.addEventListener('click', function (e) { e.preventDefault(); var b = self.root.querySelector('[data-cfo-size-block]'); if (b) b.scrollIntoView({ behavior: 'smooth', block: 'center' }); });
    });
    this.bindGallery();
    this.bindSticky();
  };

  Controller.prototype.destroy = function () {
    if (this._io) this._io.disconnect();
  };

  // ---------- Cross-section trigger delegation ----------
  // Any element (in any section) with [data-cfo-trigger] operates
  // the nearest MAIN controller (or the only one on the page).
  //   data-cfo-trigger="add"            -> add current selection
  //   data-cfo-trigger="add-with-black" -> turn add-on ON, then add
  //   data-cfo-trigger="add-pack-only"  -> turn add-on OFF, then add
  //   data-cfo-trigger="select-addon"   -> turn add-on ON (no add)
  //   data-cfo-trigger="scroll-to-buy"  -> scroll to size block
  function controllerFor(el) {
    var host = el.closest('[data-cfo-root]');
    if (host && registry[host.getAttribute('data-cfo-root')]) return registry[host.getAttribute('data-cfo-root')];
    var ids = Object.keys(registry);
    if (ids.length === 1) return registry[ids[0]];
    // multiple: pick the first main on the page
    return ids.length ? registry[ids[0]] : null;
  }

  // Size pills living in presentational sections (no [data-cfo-root]),
  // e.g. the final-CTA and Noir size blocks, drive the MAIN controller.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.core-four-originalz-pdp [data-cfo-size]');
    if (!btn || btn.closest('[data-cfo-root]') || btn.disabled) return;
    var ctrl = controllerFor(btn);
    if (ctrl) ctrl.setSize(btn.getAttribute('data-cfo-size'));
  });

  document.addEventListener('click', function (e) {
    var el = e.target.closest('[data-cfo-trigger]');
    if (!el) return;
    var action = el.getAttribute('data-cfo-trigger');
    var ctrl = controllerFor(el);
    if (!ctrl) { console.warn('[CFO] trigger "' + action + '" fired but no MAIN section is present on this page.'); return; }
    e.preventDefault();
    switch (action) {
      case 'add': ctrl.addToCart(); break;
      case 'add-with-black': ctrl.setAddon(true, { fromTrigger: true }); if (ctrl.state.addon) ctrl.addToCart(); break;
      case 'add-pack-only': ctrl.setAddon(false); ctrl.addToCart(); break;
      case 'select-addon': ctrl.setAddon(true, { fromTrigger: true }); break;
      case 'scroll-to-buy':
        var b = ctrl.root.querySelector('[data-cfo-size-block]'); if (b) b.scrollIntoView({ behavior: 'smooth', block: 'center' }); break;
    }
  });

  // ---------- Init / lifecycle ----------
  function initRoot(root) {
    var id = root.getAttribute('data-cfo-root');
    if (!id) return;
    if (registry[id]) { try { registry[id].destroy(); } catch (e) {} }
    registry[id] = new Controller(root);
  }
  function initAll(scope) {
    (scope || document).querySelectorAll('[data-cfo-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  // Shopify theme editor lifecycle
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector ? (e.target.matches && e.target.matches('[data-cfo-root]') ? e.target : e.target.querySelector('[data-cfo-root]')) : null;
    if (root) initRoot(root); else initAll(e.target);
    // A presentational section (e.g. final-CTA size pills) may have been
    // re-rendered — re-sync availability + active state from controllers.
    Object.keys(registry).forEach(function (id) {
      var c = registry[id];
      c.renderSizes();
      if (c.state.size) {
        c.allSizeButtons().forEach(function (b) {
          b.classList.toggle('cfo-active', b.getAttribute('data-cfo-size') === c.state.size);
        });
      }
    });
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-cfo-root]');
    if (root) { var id = root.getAttribute('data-cfo-root'); if (registry[id]) { registry[id].destroy(); delete registry[id]; } }
  });

  // expose for debugging / external hooks
  window.DrawlzCFO = { registry: registry, init: initAll };
})();
